import { type TableMeta } from "@tanstack/react-table";
import { type FC, useCallback, useMemo } from "react";

import { InputSelect } from "../../../InputSelect";
import {
	type DataGridColumnDefPerson,
	type PersonDefaultValues,
	personDefaultValues,
} from "../../useColumnState";
import { StandardColumnOptions } from "../StandardColumnOptions";

export const EditPersonOptions: FC<{
	headerID: string;
	columnDef: DataGridColumnDefPerson<unknown, unknown>;
	updateColumn: TableMeta<unknown>["updateColumn"];
	updateColumnData: TableMeta<unknown>["updateColumnData"];
}> = ({ headerID, columnDef, updateColumn }) => {
	const fieldTypeInfo = useMemo(() => {
		return columnDef.fieldTypeInfo || {};
	}, [columnDef.fieldTypeInfo]);

	const changeDefaultValue = useCallback(
		(value: PersonDefaultValues) => {
			if (!updateColumn) {
				return;
			}

			const newFieldInfo: DataGridColumnDefPerson<unknown, unknown>["fieldTypeInfo"] =
				Object.assign({}, fieldTypeInfo, { defaultValue: value });

			const newColumnDef: DataGridColumnDefPerson<unknown, unknown> = Object.assign(
				{},
				columnDef,
				{ fieldTypeInfo: newFieldInfo }
			);

			updateColumn(headerID, newColumnDef);
		},
		[fieldTypeInfo, headerID, columnDef, updateColumn]
	);

	const value = fieldTypeInfo?.defaultValue || "Empty";
	return (
		<div className="flex flex-col space-y-4 pt-2">
			<div className="flex flex-col space-y-2">
				<div>
					<label
						className="text-sm font-bold text-tertiary"
						htmlFor="column-default-value"
					>
						Value for new rows
					</label>
				</div>
				<InputSelect.Root value={value} onValueChange={changeDefaultValue}>
					<InputSelect.Trigger id="column-default-value">
						<InputSelect.Value>{value}</InputSelect.Value>
					</InputSelect.Trigger>
					<InputSelect.Portal>
						<InputSelect.Content>
							<InputSelect.ScrollUpButton />
							<InputSelect.Viewport>
								{personDefaultValues.map((defaultValue) => (
									<InputSelect.Item key={defaultValue} value={defaultValue}>
										{defaultValue}
									</InputSelect.Item>
								))}
							</InputSelect.Viewport>
							<InputSelect.ScrollDownButton />
						</InputSelect.Content>
					</InputSelect.Portal>
				</InputSelect.Root>
			</div>
			<StandardColumnOptions
				headerID={headerID}
				columnDef={columnDef}
				updateColumn={updateColumn}
			/>
		</div>
	);
};
